import React, {
  useRef,
  useMemo,
  useCallback,
  useState,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import throttle from 'lodash/throttle';
import { BiData, TopNavClickBiEvent } from '@wix/da-bi/pkg/events';
import { Url } from '@wix/da-url';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import NoteIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import NoteOutlineIcon from '@wix/da-ds/pkg/Icons/24x24/MailOutline';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import IconButtonWithNotification, {
  type IconButtonWithNotificationProps,
} from '@wix/da-ds/pkg/Buttons/IconButtonWithNotification';
import { usePushModal } from '../../../utils/hooks/usePushModal';
import { ModalType } from '../../../Modals/redux/types';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useVerification } from '../../../VerificationPopup/withVerification';
import LoadableDesktopNotesPanel from './DesktopNotesPanel/LoadableDesktopNotesPanel';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { isBrowserHoverCapable } from '../../../utils/browser';

import s from './NotesButtonAndDropdown.scss';

interface NotesButtonProps
  extends Omit<
    IconButtonWithNotificationProps,
    | 'icon'
    | 'size'
    | 'notificationColor'
    | 'biData'
    | 'href'
    | 'onClick'
    | 'aria-label'
  > {
  action:
    | Required<Pick<IconButtonWithNotificationProps, 'onClick'>>
    | Required<Pick<IconButtonWithNotificationProps, 'href'>>;
  iconWithOutline?: boolean;
}

const NotesButton: React.FC<NotesButtonProps> = ({
  count = 0,
  action,
  iconWithOutline,
  ...rest
}) => {
  const { t } = useTranslation();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();

  /**
   * We need to be sure that users with screen readers know that this button will instead open the verification popup.
   *
   * Avoids confusion when they can't find their notes.
   */
  const actionAndLabel = isVerificationNeeded
    ? {
        onClick: () => pushVerificationPopup('notes'),
        'aria-label': t('siteHeader.a11y.openAccountVerificationDialog'),
      }
    : {
        ...action,
        'aria-label': t(
          count > 0
            ? 'siteHeader.a11y.notes.withItems'
            : 'siteHeader.a11y.notes.withoutItems',
          { formattedCount: count }
        ),
      };

  return (
    <IconButtonWithNotification
      icon={iconWithOutline ? NoteOutlineIcon : NoteIcon}
      size="medium"
      notificationColor="red"
      count={count}
      biData={BiData<TopNavClickBiEvent>({
        evid: 700,
        link_name: 'notes',
        indicator: count,
      })}
      {...actionAndLabel}
      {...rest}
    />
  );
};

function useMergedCounts(hasNewMsg) {
  const { counts: sessionCounts } = useContext(SessionContext);
  let mergedCount = (sessionCounts?.notes ?? 0) + (sessionCounts?.chats ?? 0);
  if (hasNewMsg && (sessionCounts?.chats ?? 0) === 0) {
    mergedCount = mergedCount + 1;
  }

  return mergedCount;
}

export interface Props
  extends Pick<NotesButtonProps, 'iconButtonClassName' | 'numberClassName'> {
  className?: string;
  hasNewMsg: boolean;
  disableDropdown?: boolean;
}

const dropdownId = 'site-header-notes';
const Desktop: React.FC<Props> = ({
  className,
  hasNewMsg,
  disableDropdown,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { isVerificationNeeded } = useVerification();
  const rootRef = useRef<HTMLDivElement>(null);
  const enterTimerRef = useRef<any>(undefined);
  const mergedCount = useMergedCounts(hasNewMsg);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleOpenCrossDevice = useCallback(e => {
    if (!isBrowserHoverCapable()) {
      setIsOpen(true);
      e.preventDefault();
    }
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const throttledMouseLeave = useMemo(
    () =>
      throttle(() => {
        clearTimeout(enterTimerRef.current);
        handleClose();
      }, 300),
    [handleClose]
  );

  const onMouseEnter = () => {
    enterTimerRef.current = setTimeout(() => {
      handleOpen();
    }, 100);
  };

  const onClosed = useCallback(() => {
    throttledMouseLeave();
  }, [throttledMouseLeave]);

  return (
    <div
      className={c(s['desktop-root'], className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onClosed}
      ref={rootRef}
    >
      {!isVerificationNeeded && (
        <TextButton
          className={s['a11y-toggle']}
          size="medium"
          variant="main"
          aria-haspopup={true}
          aria-expanded={isOpen}
          aria-controls={dropdownId}
          onClick={isOpen ? handleClose : handleOpen}
        >
          {t('siteHeader.notes.open')}
        </TextButton>
      )}
      <NotesButton
        count={mergedCount}
        action={{
          onClick: handleOpenCrossDevice,
          href: Url.notesHomeLink(),
        }}
        {...rest}
      />
      <ErrorBoundary debugComponent="NotesButtonAndDropdown">
        {isMounted && isOpen && !disableDropdown && (
          <LoadableDesktopNotesPanel
            id={dropdownId}
            className={c(s['dropdown'])}
            onClose={onClosed}
          />
        )}
      </ErrorBoundary>
    </div>
  );
};

const Mobile: React.FC<Props> = ({
  className,
  hasNewMsg,
  disableDropdown,
  ...rest
}) => {
  const pushModal = usePushModal(ModalType.MOBILE_NOTES_DRAWER);
  const mergedCount = useMergedCounts(hasNewMsg);

  return (
    <div className={className}>
      <NotesButton
        count={mergedCount}
        action={
          disableDropdown
            ? {
                href: Url.notesHomeLink(),
              }
            : {
                onClick: pushModal,
              }
        }
        {...rest}
      />
    </div>
  );
};

export const NotesButtonAndDropdown: React.FC<
  React.PropsWithChildren<Props>
> = props => {
  const isMobile = useContext(MobileContext);
  return isMobile ? <Mobile {...props} /> : <Desktop {...props} />;
};
NotesButtonAndDropdown.displayName = 'NotesButtonAndDropdown';

export default NotesButtonAndDropdown;
